import type React from "react"
import styled, { css } from "styled-components"
import { Icon, type IconProps } from "~/components/Icon"
import type { CSSButtonType } from "~/types/style"
import type { FontAwesomeIcon } from "~/ur-react-components"

interface BaseButtonProps {
  width?: string
  size?: "small" | "medium"
}

const BaseButton = styled.button<BaseButtonProps>`
  width: ${(props) => props.width ?? "auto"};

  // The design uses inner borders, so we have to adjust the padding accordingly
  padding: 12px 24px;
  background: white;

  border-radius: 8px;

  outline: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  // Gap between icon and text6
  gap: 10px;

  outline: none;
  border: 2px solid ${(props) => props.theme.colors.gray95};

  color: ${(props) => props.theme.colors.primary40};

  cursor: pointer;

  box-sizing: border-box;

  &:hover {
    background: ${(props) => props.theme.colors.primary95};
    border: 2px solid ${(props) => props.theme.colors.gray90};
    color: ${(props) => props.theme.colors.primary40};
  }

  &:focus {
    background: ${(props) => props.theme.colors.primary95};
    border: 2px solid ${(props) => props.theme.colors.gray90};
    color: ${(props) => props.theme.colors.primary40};
  }

  &:active {
    background: ${(props) => props.theme.colors.gray95};
    border: 2px solid ${(props) => props.theme.colors.primary40};
    color: ${(props) => props.theme.colors.primary40};
  }

  &:disabled {
    background: ${(props) => props.theme.colors.white};
    border: 2px solid ${(props) => props.theme.colors.gray60};
    color: ${(props) => props.theme.colors.gray60};
    cursor: not-allowed;
  }

  ${(props) =>
    props.size === "small" &&
    css`
      padding: 6px 24px;
    `}
`

const InnerText = styled.span`
  font-size: 14px;
  line-height: 150%;
  font-weight: 500;
  letter-spacing: 0.01em;
`

export interface OutlineButtonProps {
  width?: string
  size?: "small" | "medium"
  leftIcon?: FontAwesomeIcon
  rightIcon?: FontAwesomeIcon

  leftIconProps?: IconProps
  rightIconProps?: IconProps

  onClick?: () => void

  disabled?: boolean
  type?: CSSButtonType
}

export const OutlineButton: React.CFC<OutlineButtonProps> = ({
  width,
  size = "medium",
  children,
  leftIcon,
  rightIcon,
  leftIconProps,
  rightIconProps,
  onClick,
  disabled,
  type,
}) => {
  return (
    <BaseButton
      width={width}
      onClick={onClick}
      disabled={disabled}
      size={size}
      type={type}
    >
      {leftIcon && <Icon icon={leftIcon} {...leftIconProps} />}
      <InnerText>{children}</InnerText>
      {rightIcon && <Icon icon={rightIcon} {...rightIconProps} />}
    </BaseButton>
  )
}
